// Account.js
import React, { useState } from 'react';
import { useAuth } from './context/AuthContext';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { auth, db } from './firebase';

export default function Account() {
  const { user, logout } = useAuth() || {};

  const [mode, setMode] = useState('signin');
  const [username, setUsername] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSubmit = async () => {
    const trimmedEmail = emailInput.trim();
    const trimmedPassword = password.trim();
    const trimmedUsername = username.trim();

    if (!trimmedEmail || !trimmedPassword || (mode === 'signup' && (!trimmedUsername || !confirmPassword.trim()))) {
      alert('Please fill in all required fields.');
      return;
    }

    if (!emailRegex.test(trimmedEmail)) {
      alert('Please enter a valid email address.');
      return;
    }

    if (mode === 'signup') {
      if (trimmedUsername.length < 3) {
        alert('Username must be at least 3 characters.');
        return;
      }
      if (trimmedPassword.length < 6) {
        alert('Password must be at least 6 characters.');
        return;
      }
      if (trimmedPassword !== confirmPassword.trim()) {
        alert('Passwords do not match.');
        return;
      }

      try {
        await createUserWithEmailAndPassword(auth, trimmedEmail, trimmedPassword);

        await setDoc(
          doc(db, 'users', trimmedEmail),
          {
            username: trimmedUsername,
            email: trimmedEmail,
            createdAt: serverTimestamp()
          },
          { merge: true }
        );

        setUsername('');
        setEmailInput('');
        setPassword('');
        setConfirmPassword('');
        alert('✅ Account created successfully!');
      } catch (error) {
        if (error.code === 'auth/email-already-in-use') {
          alert('This email is already registered. Try signing in instead.');
        } else {
          console.error('Signup error:', error);
          alert('Something went wrong saving your account. Try again.');
        }
      }
    } else {
      try {
        const userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, trimmedPassword);
        const u = userCredential.user;

        const userDoc = await getDoc(doc(db, 'users', u.email));
        const userData = userDoc.exists() ? userDoc.data() : {};
        console.log('✅ Logged in as:', userData.username || u.email);

        const storedPicks = localStorage.getItem('draftPicks_temp');
        if (storedPicks) {
          try {
            const parsed = JSON.parse(storedPicks);

            // Supports either old shape { picks, teams } or just arrays saved separately
            if (parsed?.picks && parsed?.teams) {
              await setDoc(
                doc(db, 'users', u.email),
                {
                  picks: parsed.picks,
                  teams: parsed.teams
                },
                { merge: true }
              );
              localStorage.removeItem('draftPicks_temp');
              console.log('✅ Synced draft picks to Firestore');
            }
          } catch (err) {
            console.error('❌ Failed to sync picks:', err);
          }
        }

        setEmailInput('');
        setPassword('');
      } catch (error) {
        alert('Login failed: ' + error.message);
      }
    }
  };

  return (
    <div className="max-w-md mx-auto mt-8 bg-white shadow p-6 rounded">
      <h2 className="text-xl font-bold mb-4">
        {user ? 'Account' : 'Sign In or Sign Up'}
      </h2>

      {user ? (
        <div>
          <p className="mb-2">
            <strong>Signed in as:</strong> {user.displayName || user.email}
          </p>
          <button
            onClick={logout}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Log Out
          </button>
        </div>
      ) : (
        <div>
          {mode === 'signup' && (
            <input
              type="text"
              placeholder="Username"
              className="w-full mb-2 p-2 border rounded"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          )}

          <input
            type="email"
            placeholder="Email"
            className="w-full mb-2 p-2 border rounded"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
          />

          <div className="relative mb-2">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              className="w-full p-2 border rounded pr-10"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-2 text-gray-600 text-sm"
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>

          {mode === 'signup' && (
            <div className="relative mb-2">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm Password"
                className="w-full p-2 border rounded pr-10"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-2 top-2 text-gray-600 text-sm"
              >
                {showConfirmPassword ? '🙈' : '👁️'}
              </button>
            </div>
          )}

          <button
            onClick={handleSubmit}
            className="bg-blue-500 text-white px-4 py-2 rounded w-full"
          >
            {mode === 'signup' ? 'Create Account' : 'Sign In'}
          </button>

          <div className="mt-4 text-sm text-center">
            {mode === 'signup' ? (
              <>
                Already have an account?{' '}
                <button onClick={() => setMode('signin')} className="text-blue-600 underline">
                  Sign In
                </button>
              </>
            ) : (
              <>
                New user?{' '}
                <button onClick={() => setMode('signup')} className="text-blue-600 underline">
                  Sign Up
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

